import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import axios from 'axios';

import { detectFaces, drawResults } from '../../helpers/faceApi';
import Button from '../Button/Button';
import Webcam from 'react-webcam';
import EmotionBars, { simulateExpressions } from '../EmotionBars/EmotionBars';
import EmotionChart from '../EmotionChart/EmotionChart';
import './Camera.css';

// ── Category Definitions ──────────────────────────────────────────
const CATEGORIES = ['All', 'Technology', 'Business', 'Sports', 'Politics', 'Health', 'Science', 'Entertainment', 'World'];

const CATEGORY_KEYWORDS = {
  Technology:    ['tech', 'ai', 'software', 'digital', 'app', 'robot', 'cyber', 'data', 'developer', 'computer', 'machine learning', 'startup', 'internet'],
  Business:      ['market', 'economy', 'finance', 'stock', 'trade', 'company', 'corporate', 'gdp', 'revenue', 'bank', 'invest', 'fund', 'startup'],
  Sports:        ['sport', 'cricket', 'football', 'match', 'tournament', 'player', 'team', 'olympic', 'league', 'champion', 'athlete', 'stadium'],
  Politics:      ['politic', 'government', 'election', 'minister', 'parliament', 'vote', 'policy', 'president', 'congress', 'senate', 'bilateral', 'diplomat'],
  Health:        ['health', 'medical', 'doctor', 'hospital', 'disease', 'vaccine', 'drug', 'treatment', 'patient', 'mental', 'cancer', 'covid', 'virus'],
  Science:       ['science', 'research', 'space', 'nasa', 'climate', 'study', 'discovery', 'biology', 'physics', 'chemistry', 'experiment', 'satellite'],
  Entertainment: ['movie', 'film', 'music', 'celebrity', 'actor', 'award', 'oscar', 'netflix', 'streaming', 'concert', 'album', 'show', 'singer'],
  World:         ['world', 'global', 'international', 'war', 'conflict', 'un', 'nato', 'refugee', 'humanitarian', 'summit', 'treaty', 'border'],
};

// ── Bookmark Helpers ──────────────────────────────────────────────
const BOOKMARK_KEY = 'emonews-bookmarks';

const getBookmarks = () => {
  try { return JSON.parse(localStorage.getItem(BOOKMARK_KEY) || '[]'); }
  catch { return []; }
};

const toggleBookmark = (article) => {
  const current = getBookmarks();
  const idx = current.findIndex(b => b.url === article.url);
  let updated;
  if (idx >= 0) {
    updated = current.filter((_, i) => i !== idx);
  } else {
    updated = [{ ...article, bookmarkedAt: Date.now() }, ...current];
  }
  localStorage.setItem(BOOKMARK_KEY, JSON.stringify(updated));
  return updated;
};

// ── Category matcher ──────────────────────────────────────────────
const matchesCategory = (article, category) => {
  if (category === 'All') return true;
  const keywords = CATEGORY_KEYWORDS[category] || [];
  const text = `${article.title || ''} ${article.description || ''}`.toLowerCase();
  return keywords.some(kw => text.includes(kw));
};

// ── Skeleton Card ─────────────────────────────────────────────────
const SkeletonCard = () => (
  <div className="news-card skeleton-card" aria-hidden="true">
    <div className="skeleton skeleton-img" />
    <div className="news-card-body">
      <div className="skeleton skeleton-title" />
      <div className="skeleton skeleton-para" />
      <div className="skeleton skeleton-para skeleton-para--short" />
      <div className="skeleton-bars">
        {[...Array(3)].map((_, i) => <div key={i} className="skeleton skeleton-bar" />)}
      </div>
    </div>
  </div>
);

// ── News Card ─────────────────────────────────────────────────────
const NewsCard = ({ article, index, detectedEmotion, bookmarks, onToggleBookmark }) => {
  const expressions = useMemo(
    () => simulateExpressions(detectedEmotion, index + 1),
    [detectedEmotion, index]
  );
  const isBookmarked = bookmarks.some(b => b.url === article.url);

  return (
    <div
      className="news-card animate-fade-in"
      style={{ animationDelay: `${index * 0.04}s` }}
      role="article"
      aria-label={article.title}
    >
      {/* Bookmark Button */}
      <button
        className={classnames('bookmark-btn', isBookmarked && 'bookmark-btn--active')}
        onClick={() => onToggleBookmark(article)}
        aria-label={isBookmarked ? 'Remove bookmark' : 'Bookmark this article'}
        title={isBookmarked ? 'Remove bookmark' : 'Save article'}
      >
        {isBookmarked ? '★' : '☆'}
      </button>

      {/* Image */}
      {article.urlToImage && (
        <div className="news-card-img-wrapper img-hover-zoom">
          <img src={article.urlToImage} alt={article.title || 'News'} loading="lazy" />
        </div>
      )}

      <div className="news-card-body">
        <a
          href={article.url}
          target="_blank"
          rel="noopener noreferrer"
          className="news-card-title"
        >
          {article.title}
        </a>

        {article.description && (
          <p className="news-card-desc">{article.description}</p>
        )}

        <div className="news-card-footer">
          {article.source?.name && (
            <span className="news-card-source">{article.source.name}</span>
          )}
          <a
            href={article.url}
            target="_blank"
            rel="noopener noreferrer"
            className="read-more"
          >
            Read More →
          </a>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────
const Camera = ({ photoMode }) => {
  const camera = useRef(null);
  const cameraCanvas = useRef(null);
  const newsFeedRef = useRef(null);

  const [news, setNews] = useState([]);
  const [detectedEmotion, setDetectedEmotion] = useState('');
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [bookmarks, setBookmarks] = useState(() => getBookmarks());
  const [isListening, setIsListening] = useState(false);

  // Performance: prevent concurrent detections
  const isDetectingRef = useRef(false);

  // ── Refresh bookmarks from localStorage whenever they might change ──
  useEffect(() => {
    const handleStorage = () => setBookmarks(getBookmarks());
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const handleToggleBookmark = useCallback((article) => {
    const updated = toggleBookmark(article);
    setBookmarks(updated);
    // Dispatch event so sidebar badge updates
    window.dispatchEvent(new Event('storage'));
  }, []);

  // ── Fetch news for emotion (UNCHANGED logic) ─────────────────────
  const getNewsForEmotion = useCallback(async (emotion) => {
    if (!emotion) return;

    setIsLoading(true);
    setDetectedEmotion(emotion);
    setActiveCategory('All'); // reset filter on new emotion scan

    let query = 'general';
    if (emotion === 'sad') {
      query = 'happiness OR uplifting OR joy OR success OR positive';
    } else if (emotion === 'angry') {
      query = 'comedy OR funny OR jokes OR stand-up OR relaxation';
    } else if (emotion === 'happy' || emotion === 'neutral') {
      query = 'general news OR world technology OR science OR nature OR society';
    } else if (emotion === 'surprised') {
      query = 'amazing facts OR discovery OR space OR breakthrough';
    }

    try {
      const API_URL = process.env.REACT_APP_API_URL || '/api';
      const response = await axios.get(`${API_URL}/news`, {
        params: { q: query, language: 'en', pageSize: 40 }
      });

      let articles = response.data.articles || [];

      // Shuffle news to keep it fresh
      articles = articles.sort(() => Math.random() - 0.5);

      setNews(articles.slice(0, 20));

      // Smoothly scroll to news feed
      setTimeout(() => {
        if (newsFeedRef.current) {
          const yOffset = -100;
          const y = newsFeedRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
    } catch (error) {
      console.error('News API Error:', error);

      // Fallback for 426 or no response
      if (error.response?.status === 426 || !error.response) {
        setNews([
          {
            title: `[NEURAL SIMULATION] Optimizing focus for ${emotion} states`,
            description: `System detected that external news feed is restricted in production. Displaying high-fidelity simulated intel for your current ${emotion} frequency.`,
            url: '#',
            urlToImage: 'https://images.unsplash.com/photo-1620712943543-bcc4628c6bb8?auto=format&fit=crop&q=80&w=800',
            source: { name: 'EmoNews AI' },
          },
          {
            title: 'Psychology of Emotional Calibration',
            description: 'New research suggests that matching news content to emotional resonance can improve cognitive output by 40%.',
            url: '#',
            urlToImage: 'https://images.unsplash.com/photo-1507413245164-6160d8298b31?auto=format&fit=crop&q=80&w=800',
            source: { name: 'EmoNews AI' },
          },
        ]);
      } else {
        setNews([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ── Voice Command (Speech Recognition) ──────────────────────────
  const handleVoiceCommand = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Your browser does not support Voice Input. Please use Chrome or Edge.");
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onerror = () => setIsListening(false);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript.toLowerCase();
      console.log('Voice input:', transcript);
      
      if (transcript.includes('happy') || transcript.includes('joy') || transcript.includes('good') || transcript.includes('smile')) {
        getNewsForEmotion('happy');
      } else if (transcript.includes('sad') || transcript.includes('cry') || transcript.includes('bad') || transcript.includes('depress')) {
        getNewsForEmotion('sad');
      } else if (transcript.includes('angry') || transcript.includes('mad') || transcript.includes('hate') || transcript.includes('furious')) {
        getNewsForEmotion('angry');
      } else if (transcript.includes('neutral') || transcript.includes('calm') || transcript.includes('normal') || transcript.includes('okay')) {
        getNewsForEmotion('neutral');
      } else {
        alert(`I heard "${transcript}", but couldn't match it to an emotion. Try saying Happy, Sad, Angry, or Neutral.`);
      }
    };

    recognition.start();
  }, [getNewsForEmotion]);

  // ── Face Detection (UNCHANGED logic) ────────────────────────────
  const getFaces = useCallback(async () => {
    if (
      isDetectingRef.current ||
      !isCameraOn ||
      !camera.current ||
      !camera.current.video ||
      camera.current.video.readyState !== 4
    ) return;

    isDetectingRef.current = true;
    try {
      const faces = await detectFaces(camera.current.video);

      if (cameraCanvas.current && faces) {
        await drawResults(camera.current.video, cameraCanvas.current, faces);
      }

      if (faces && faces.length > 0) {
        const expressions = faces[0].expressions;
        const dominantEmotion = Object.keys(expressions).reduce((a, b) =>
          expressions[a] > expressions[b] ? a : b
        );
        if (expressions[dominantEmotion] > 0.65) {
          getNewsForEmotion(dominantEmotion);
          setIsCameraOn(false);
        }
      }
    } catch (error) {
      console.error('Detection Error:', error);
    } finally {
      isDetectingRef.current = false;
    }
  }, [isCameraOn, getNewsForEmotion]);

  useEffect(() => {
    let interval;
    if (!photoMode && isCameraOn) {
      interval = setInterval(getFaces, 1000);
    }
    return () => clearInterval(interval);
  }, [photoMode, isCameraOn, getFaces]);

  // ── Filtered News ────────────────────────────────────────────────
  const filteredNews = useMemo(() =>
    news.filter(a => matchesCategory(a, activeCategory)),
    [news, activeCategory]
  );

  const MANUAL_EMOTIONS = [
    { id: 'happy',    label: '😊 Happy'   },
    { id: 'sad',      label: '😢 Sad'     },
    { id: 'angry',    label: '😡 Angry'   },
    { id: 'neutral',  label: '😐 Neutral' },
  ];

  return (
    <div className="camera">
      <div className="container">
        <div className="section-header animate-fade-in">
          <h2>Mood-Balanced Intel</h2>
          {detectedEmotion && (
            <span className="section-header__badge">
              {detectedEmotion.charAt(0).toUpperCase() + detectedEmotion.slice(1)} Mode
            </span>
          )}
        </div>

        <div className="main-layout">
          {/* ── Left Panel: Camera + Controls ── */}
          <aside className="camera-preview">
            {/* Scanner Card */}
            <div className="camera-block glass-card">
              <div className="block-label">EMOTION SCAN</div>
              <div className="camera__wrapper small-cam">
                {isCameraOn ? (
                  <>
                    <Webcam
                      audio={false}
                      ref={camera}
                      width="100%"
                      height="auto"
                      mirrored={true}
                      aria-label="Webcam feed for emotion detection"
                    />
                    <canvas
                      className={classnames('webcam-overlay', photoMode && 'webcam-overlay--hidden')}
                      ref={cameraCanvas}
                      aria-hidden="true"
                    />
                  </>
                ) : (
                  <div className="camera__off">
                    <p>{detectedEmotion ? 'ANALYSIS COMPLETE' : 'SYSTEM READY'}</p>
                    {detectedEmotion && (
                      <div className="last-emotion-tag">{detectedEmotion.toUpperCase()}</div>
                    )}
                  </div>
                )}
                {isCameraOn && <div className="scanner-bezel" aria-hidden="true" />}
              </div>

              <div className="camera__controls">
                <Button
                  onClick={() => setIsCameraOn(!isCameraOn)}
                  className={classnames('toggle-btn btn-small', !isCameraOn && 'button--primary')}
                  aria-label={isCameraOn ? 'Stop emotion scan' : 'Start emotion scan'}
                >
                  {isCameraOn
                    ? <><span role="img" aria-hidden>🛑</span> STOP SCAN</>
                    : <><span role="img" aria-hidden>⚡</span> START SCAN</>
                  }
                </Button>
              </div>
            </div>

            {/* Manual Selector Card */}
            <div className="manual-selector glass-card">
              <div className="block-label">MANUAL SELECTION</div>
              <div className="selector-content">
                <div className="emotion-buttons">
                  {MANUAL_EMOTIONS.map(emo => (
                    <button
                      key={emo.id}
                      className={classnames('emo-btn-large', detectedEmotion === emo.id && `active-${emo.id}`)}
                      onClick={() => getNewsForEmotion(emo.id)}
                      disabled={isLoading}
                      aria-label={`Scan for ${emo.id} emotion news`}
                      aria-pressed={detectedEmotion === emo.id}
                    >
                      <span className="emo-icon" role="img" aria-hidden>{emo.label.split(' ')[0]}</span>
                      <span className="emo-text">{emo.label.split(' ')[1]}</span>
                    </button>
                  ))}
                </div>
                
                {/* Voice Input Button */}
                <div className="voice-input-container" style={{ marginTop: '1rem', textAlign: 'center' }}>
                  <button 
                    className={classnames('voice-btn', isListening && 'listening')} 
                    onClick={handleVoiceCommand}
                    title="Say an emotion (e.g. 'Happy' or 'Angry')"
                  >
                    <span role="img" aria-label="microphone" style={{ fontSize: '1.2rem', marginRight: '8px' }}>🎤</span>
                    {isListening ? 'Listening...' : 'Use Voice Command'}
                  </button>
                  {isListening && <div className="voice-pulse-ring"></div>}
                </div>
              </div>
            </div>

            {/* Analytics Chart — updates when category changes */}
            {news.length > 0 && (
              <EmotionChart
                articles={filteredNews}
                detectedEmotion={detectedEmotion}
                title="Emotion Analytics"
              />
            )}
          </aside>

          {/* ── Right Panel: News Feed ── */}
          <main className="news-feed" ref={newsFeedRef}>
            {/* Category Filter Chips */}
            {news.length > 0 && !isLoading && (
              <div className="category-filter" role="group" aria-label="Filter news by category">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    className={classnames('filter-chip', activeCategory === cat && 'active')}
                    onClick={() => setActiveCategory(cat)}
                    aria-pressed={activeCategory === cat}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {/* Result count */}
            {news.length > 0 && !isLoading && (
              <p className="news-result-count">
                {filteredNews.length} article{filteredNews.length !== 1 ? 's' : ''}
                {activeCategory !== 'All' ? ` in ${activeCategory}` : ''}
              </p>
            )}

            <div className="news-grid">
              {isLoading ? (
                [...Array(6)].map((_, i) => <SkeletonCard key={i} />)
              ) : filteredNews.length > 0 ? (
                filteredNews.map((article, index) => (
                  <NewsCard
                    key={`${article.url}-${index}`}
                    article={article}
                    index={index}
                    detectedEmotion={detectedEmotion}
                    bookmarks={bookmarks}
                    onToggleBookmark={handleToggleBookmark}
                  />
                ))
              ) : news.length > 0 ? (
                <div className="no-results glass-card animate-fade-in">
                  <span role="img" aria-label="no results" style={{ fontSize: '2.5rem' }}>🔍</span>
                  <h3>No articles in {activeCategory}</h3>
                  <p>Try selecting a different category or scan a new emotion.</p>
                  <button className="filter-chip" onClick={() => setActiveCategory('All')}>
                    Show All Articles
                  </button>
                </div>
              ) : null}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Camera;
